import React, { FC, ReactNode } from "react";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { Input } from "@UI/Components/Input/Input";
import { IconButton } from "@UI/Components/Button/Button";
import { useNumberInput } from "@UI/Components/NumberInput/NumberInput";
import { Plus } from "@UI/Components/Icons/Plus";
import { Minus } from "@UI/Components/Icons/Minus";
import { QuestionFormControl } from "../QuestionFormControl/QuestionFormControl";
import { BaseQuestionProps } from "../types";

export interface NumberQuestionProps extends BaseQuestionProps {
  label?: string;
  value: number | null;
  /** `null` when the field is emptied — the answer cleared, not a 0. */
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  badge?: ReactNode;
}

const stepperButtonProps = {
  isRound: true,
  bg: "gray.300",
  boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.10)",
};

export const NumberQuestion: FC<NumberQuestionProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  precision = 0,
  badge,
  isRequired = false,
  isInvalid = false,
  errorMessage = "",
  isDisabled = false,
}) => {
  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } = useNumberInput({
    // "" is what `useCounter` takes for "no value" — an unanswered question
    // shows the placeholder rather than a literal 0.
    value: value ?? "",
    onChange: (valueAsString, valueAsNumber) => {
      // An emptied field has to reach the parent, or the controlled `value`
      // springs back to the number just erased.
      if (valueAsString === "") onChange(null);
      // A half-typed number ("-", ".") parses to NaN — nothing to report yet.
      else if (!Number.isNaN(valueAsNumber)) onChange(valueAsNumber);
    },
    min,
    max,
    step,
    precision,
    isDisabled,
    // Chakra otherwise rewrites anything under `min` up to `min` on blur,
    // turning a typed 0 into a 1 — an answer the visitor never gave.
    clampValueOnBlur: false,
  });

  const { onBlur: onInputBlur, ...inputProps } = getInputProps();

  return (
    <QuestionFormControl
      isRequired={isRequired}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
    >
      {badge && <Box mb={2}>{badge}</Box>}
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" padding={4} bg="white">
        {label && (
          <Text
            color="gray.800"
            fontSize="sm"
            fontWeight="700"
            lineHeight="150%"
            textAlign="center"
            mb={3}
          >
            {label}
          </Text>
        )}
        <Flex align="center" justify="center" gap={4} alignSelf="stretch">
          <IconButton
            aria-label="Decrease"
            icon={<Minus />}
            {...stepperButtonProps}
            {...getDecrementButtonProps()}
          />
          <Input
            textAlign="center"
            color="gray.800"
            fontFamily="heading"
            fontSize="4xl"
            fontWeight="700"
            lineHeight="120%"
            width="120px"
            {...inputProps}
            onBlur={(event) => {
              onInputBlur?.(event);
              // A 0 the config forbids reads as no answer, not as a value to
              // fail the minimum validation.
              if (value === 0 && min != null && min > 0) onChange(null);
            }}
            placeholder="0"
            _placeholder={{ color: "gray.400" }}
            sx={{ "&:focus::placeholder": { color: "transparent" } }}
          />
          <IconButton
            aria-label="Increase"
            icon={<Plus />}
            {...stepperButtonProps}
            {...getIncrementButtonProps()}
          />
        </Flex>
      </Box>
    </QuestionFormControl>
  );
};
